<script setup lang="ts">
  import type { ResolvedField } from '../model/types'
  import { computed, type Component } from 'vue'
  import {
    IconTextCursor,
    IconAlignLeft,
    IconHash,
    IconCalendarDays,
    IconSquareCheck,
    IconChevronDown,
    IconPanelTop,
    IconEyeOff,
    IconLoading,
    IconMinus,
    IconEye,
    IconPencil,
    IconLocked,
    IconAccessRole,
    IconShieldCheck,
    IconEditOff
  } from '@gat-ship/icons'
  import { DataListItem, DataListItemTitle } from '@gat-ship/vue-components'
  import { Badge } from '@gat-ship/vue-components'
  import { useAccessRoles } from '../composables/useAccessRoles'

  const props = defineProps<{
    field: ResolvedField
    loading?: boolean
  }>()

  defineEmits<{
    select: []
  }>()

  const fieldTypeIcons: Record<string, Component> = {
    text: IconTextCursor,
    multiline: IconAlignLeft,
    number: IconHash,
    datetime: IconCalendarDays,
    checkbox: IconSquareCheck,
    dropdown: IconChevronDown,
    section: IconPanelTop
  }

  const dataSource = useAccessRoles()!

  function getRoleName(roleId: string): string {
    return (
      dataSource.accessRoles.value.find((r) => r.id === roleId)?.name ?? roleId
    )
  }

  function sortedByRoleName(roleIds: string[]): string[] {
    return [...roleIds].sort((a, b) =>
      getRoleName(a).localeCompare(getRoleName(b))
    )
  }

  const fieldType = computed(() => {
    return props.field.fieldType ?? props.field.inputType
  })

  function getFieldTypeIcon(): Component {
    if (!fieldType.value) return IconMinus
    return fieldTypeIcons[fieldType.value] ?? IconMinus
  }
</script>

<template>
  <DataListItem
    :disabled="field.origin === 'system' && !field.configurable"
    :options="{
      showChevron: true
    }"
    @click:item="$emit('select')"
  >
    <DataListItemTitle class="flex w-full">
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-3">
          <IconLoading
            v-if="loading"
            class="text-muted-foreground size-4 animate-spin"
          />

          <div
            class="flex items-center gap-1"
            :class="{ 'opacity-60': field.visibilityOption === 'hidden' }"
          >
            <div class="mr-1 mt-0.5 flex flex-col items-start gap-1 self-start">
              <component :is="getFieldTypeIcon()" class="size-3.5 shrink-0" />
              <IconEyeOff
                v-if="field.visibilityOption === 'hidden'"
                class="mr-1 size-3.5 shrink-0"
              />
            </div>
            <div class="flex flex-col">
              <div class="flex items-center gap-1">
                <span class="text-muted-foreground text-xs font-normal">{{
                  fieldType
                }}</span>
              </div>
              <span class="truncate">{{ field.label }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="field.visibilityOption === 'visible-for-role'"
          class="flex items-center gap-1"
        >
          <IconEye class="text-muted-foreground mr-1 size-3.5 shrink-0" />
          <Badge
            v-for="role in sortedByRoleName(field.visibleForRoleIds ?? [])"
            :key="role"
            class="bg-purple-500/15 text-purple-500"
          >
            <IconAccessRole class="size-3.5 shrink-0" />
            <span class="text-xs font-normal">
              {{ getRoleName(role) }}
            </span>
          </Badge>
        </div>
        <div
          v-if="
            field.editableOption === 'editable-for-role' &&
            field.visibilityOption !== 'hidden'
          "
          class="flex items-center gap-1"
        >
          <IconPencil class="text-muted-foreground mr-1 size-3.5 shrink-0" />
          <Badge
            v-for="role in sortedByRoleName(field.editableForRoleIds ?? [])"
            :key="role"
            class="bg-purple-500/15 text-purple-500"
          >
            <IconAccessRole class="size-3.5 shrink-0" />
            <span class="text-xs font-normal">
              {{ getRoleName(role) }}
            </span>
          </Badge>
        </div>
      </div>
      <div class="ml-auto flex flex-col items-end gap-2 self-center">
        <span
          v-if="field.hasConfigOverride"
          class="bg-primary/15 text-primary rounded px-1.5 py-0.5 text-[10px] font-medium"
          >Modified</span
        >

        <Badge
          v-if="field.requiredByDeveloper"
          variant="outline"
          class="p-0 pl-1 pr-2"
        >
          <IconLocked class="text-muted-foreground size-3.5 shrink-0" />
          <span class="text-xs font-normal"> System Required </span>
        </Badge>
        <Badge
          v-if="field.visibilityOption === 'hidden'"
          variant="secondary"
          class="group-hover:bg-muted p-0 pl-1 pr-2"
        >
          <IconEyeOff class="text-muted-foreground size-3.5 shrink-0" />
          <span class="text-xs font-normal"> Hidden </span>
        </Badge>
        <Badge
          v-else-if="field.editableOption === 'disabled'"
          variant="secondary"
          class="p-0 pl-1 pr-2"
        >
          <IconEditOff class="text-muted-foreground size-3.5 shrink-0" />
          <span class="text-xs font-normal"> Read Only </span>
        </Badge>
        <Badge
          v-else-if="
            field.requiredOption === 'required' && !field.requiredByDeveloper
          "
          variant="secondary"
        >
          <IconShieldCheck class="text-muted-foreground size-3.5 shrink-0" />
          <span class="text-xs font-normal"> Required </span>
        </Badge>
      </div>
    </DataListItemTitle>
  </DataListItem>
</template>
